import { useStore } from '../store/StoreContext.jsx';
import { useAutoSourceSync } from '../store/useAutoSourceSync.js';
import {
  getTotalExpenses,
  getTotalTaxesPaid,
  getEffectiveTaxRate,
  formatDollars,
  isRentalLoanRow,
} from '../store/selectors.js';
import Field from './Field.jsx';
import DataTable from './DataTable.jsx';
import { SectionShell } from './FormSection.jsx';

// notes is deliberately NOT read-only: "From liabilities"/"From insurance" is
// only a seed the advisor can overwrite (seedFields on the sync below keeps
// their edit from being re-stomped by the next merge).
const EXPENSE_READONLY_COLS = ['description', 'amount'];

// standardItemDeduction is no longer listed here: it renders conditionally
// below, only when the advisor picks "Itemized" (a standard deduction has a
// known amount, so asking for it was noise).
const PRIOR_INCOME_FIELDS = [
  { key: 'taxableIncome', label: 'Taxable Income', type: 'currency', width: 'medium' },
  { key: 'capLossCarryForward', label: 'Cap Loss Carry Forward', type: 'currency', width: 'medium' },
];

const PRIOR_TAXES_FIELDS = [
  { key: 'federalTax', label: 'Federal', type: 'currency', width: 'medium' },
  { key: 'stateTax', label: 'State', type: 'currency', width: 'medium' },
  { key: 'ficaTax', label: 'FICA', type: 'currency', width: 'medium' },
];

const EXPENSES_TABLE = {
  key: 'expenses',
  title: '',
  ariaTitle: 'Other Expenses',
  columns: [
    { key: '_source', label: '', hidden: true },
    { key: 'description', label: 'Description', colClass: 'col-wide' },
    { key: 'amount', label: 'Annual Amount', type: 'currency' },
    { key: 'startDate', label: 'Start Date', type: 'date', dateRange: 'planning' },
    { key: 'endDate', label: 'End Date', type: 'date', dateRange: 'planning' },
    { key: 'cola', label: 'COLA %', type: 'percent' },
    // notes has no visible column any more: every row now carries the shared
    // note icon (DataTable), which holds more text and costs no table width.
  ],
};

function GroupRow({ label, totalText, hintText }) {
  return (
    <div className="taxexp-group-row">
      <div className="taxexp-group-label">{label}</div>
      {totalText != null && (
        <div className="taxexp-total-badge">
          <input type="text" className="taxexp-total-val" value={totalText} readOnly />
          {hintText && <span className="taxexp-total-hint">{hintText}</span>}
        </div>
      )}
    </div>
  );
}

export default function TaxesExpensesSection({ section, num }) {
  const { data, update } = useStore();
  const sectionData = data.taxesExpenses || {};

  // Annual Expenses auto-rows: liabilities (monthly payment * 12) + insurance
  // (annual premium). Both share the same _source-keyed shape.
  useAutoSourceSync({
    targetPath: 'taxesExpenses.expenses',
    matchKeyField: '_source',
    // Notes is a seed the advisor may rewrite, so it can't be trusted for
    // reclaiming; the deterministic row uids below are the primary reclaim.
    seedFields: ['notes'],
    // After a Salesforce load the _source is gone; reclaim the loaded row by
    // its deterministic uid first (survives notes edits), then by the
    // source-derived notes + description for rows saved before deterministic
    // uids existed. Only rows that came from liabilities/insurance are
    // eligible (manual expenses are left untouched).
    naturalKey: r => {
      const n = (r?.notes || '').trim().toLowerCase();
      if (n !== 'from liabilities' && n !== 'from insurance') return '';
      return n + '|' + (r?.description || '').trim().toLowerCase();
    },
    computeAutoRows: d => {
      const rows = [];
      // Deterministic, household-scoped row uids (same scheme as Employment
      // Income). Row uids survive the Salesforce round-trip, so the merge can
      // reclaim these rows on reload even after the advisor rewrites the notes.
      const hh = String(d?._uid || '').trim();
      const stableUid = key => (hh ? { _uid: `${hh}-exp-${key}` } : {});
      // ONE consolidated "Personal Liabilities" row, not one row per loan
      // (Jake, 2026-07-23: "I don't think we need to itemize our liabilities
      // twice"). A client with a mortgage, car loan, HELOC and student loan
      // used to generate four expense rows restating the Liabilities section.
      // The consolidated row is what the advisor sees AND what goes to
      // Salesforce, so the app and the record agree. Mortgages are included.
      // A RENTAL property's mortgage is left out of this row. The advisor's
      // Income (EBT) figure is entered net of that payment, so listing it as an
      // expense charges the household twice and makes the shortfall look worse
      // than it is (Jake 2026-08-05, Joe Sanchez 2026-08-10). The loan stays in
      // the Liabilities section, because it is still a real debt; it just isn't
      // a personal expense. Same predicate the cash-flow selector uses, so the
      // expense row and the KPI can never disagree.
      const liabs = Array.isArray(d?.liabilities?.items) ? d.liabilities.items : [];
      const assets = d?.assets || {};
      const liabAnnual = liabs.reduce((sum, l) => {
        if (isRentalLoanRow(l, assets)) return sum;
        const pmt = parseFloat(l?.payment);
        return sum + (Number.isFinite(pmt) && pmt > 0 ? pmt * 12 : 0);
      }, 0);
      if (liabAnnual > 0) {
        // _source stays under the `liability` prefix the cash-flow selector
        // tests for, so the consolidated row is excluded from non-debt
        // expenses exactly like the per-loan rows were.
        rows.push({
          _source: 'liability:all',
          ...stableUid('liabilities'),
          description: 'Personal Liabilities',
          amount: liabAnnual,
          notes: 'From liabilities',
          _readOnly: EXPENSE_READONLY_COLS,
        });
      }
      const policies = Array.isArray(d?.insurance?.policies) ? d.insurance.policies : [];
      policies.forEach((p, i) => {
        if (p?.annualPremium && parseFloat(p.annualPremium) > 0) {
          const key = `insurance:${p._uid || i}`;
          rows.push({
            _source: key,
            ...stableUid(key),
            description: 'Insurance Premium - ' + (p.company || 'Unknown'),
            amount: parseFloat(p.annualPremium),
            notes: 'From insurance',
            _readOnly: EXPENSE_READONLY_COLS,
          });
        }
      });
      // Renting (Assets section): a renter's monthly rent becomes an annualized
      // expense. Singleton source, so the key is just 'rent'. naturalKey doesn't
      // claim it, so after a Salesforce reload (where the rent toggle doesn't
      // round-trip yet) the saved row is reclaimed by its deterministic uid, or
      // kept as a plain manual expense for rows saved before uids existed.
      const rentMonthly = parseFloat(d?.assets?.rentMonthly);
      if (d?._assetChecks?.renting && rentMonthly > 0) {
        rows.push({
          _source: 'rent',
          ...stableUid('rent'),
          description: 'Rent',
          amount: rentMonthly * 12,
          notes: 'From rent',
          _readOnly: EXPENSE_READONLY_COLS,
        });
      }
      return rows;
    },
  });

  const renderField = f => (
    <Field
      key={f.key}
      field={f}
      value={sectionData[f.key]}
      onChange={val => update(`taxesExpenses.${f.key}`, val)}
    />
  );

  // Tax basis + deduction type (Jake, 2026-07-23). "Prior Year Tax Information"
  // was wrong whenever the prior year wasn't representative (his client sold a
  // business), so the advisor states which basis the figures are on. Deduction
  // type replaces a bare "Std/Item Deduction" amount: for the standard
  // deduction the amount is already known, so only Itemized asks for one.
  // NOTE: both selections need Salesforce fields from Srivani before they
  // persist; the amount itself reuses the existing standardItemDeduction field.
  const taxBasis = sectionData.taxBasis || 'Prior Year Actual';
  const deductionType = sectionData.deductionType || '';

  return (
    <SectionShell section={section} num={num}>
      <div className="subhead">Tax Information</div>
      <div className="choice-group" style={{ marginBottom: 10 }}>
        {['Prior Year Actual', 'Current Year Estimate'].map(opt => (
          <label key={opt} className={'choice' + (taxBasis === opt ? ' is-on' : '')}>
            <input
              type="radio"
              name="taxesExpenses.taxBasis"
              value={opt}
              checked={taxBasis === opt}
              onChange={() => update('taxesExpenses.taxBasis', opt)}
            />
            {opt}
          </label>
        ))}
      </div>
      <GroupRow label="Income & Deductions" />
      <div className="grid">{PRIOR_INCOME_FIELDS.map(renderField)}</div>
      <div className="choice-group" style={{ margin: '4px 0 10px' }}>
        {['Standard', 'Itemized'].map(opt => (
          <label key={opt} className={'choice' + (deductionType === opt ? ' is-on' : '')}>
            <input
              type="radio"
              name="taxesExpenses.deductionType"
              value={opt}
              checked={deductionType === opt}
              onChange={() => update('taxesExpenses.deductionType', opt)}
            />
            {opt} deduction
          </label>
        ))}
      </div>
      {deductionType === 'Itemized' && (
        <div className="grid">
          <Field
            field={{ key: 'standardItemDeduction', label: 'Itemized Deduction Amount', type: 'currency', width: 'medium' }}
            value={sectionData.standardItemDeduction}
            onChange={val => update('taxesExpenses.standardItemDeduction', val)}
          />
        </div>
      )}

      <GroupRow label="Taxes Paid" />
      <div className="grid">
        {PRIOR_TAXES_FIELDS.map(renderField)}
        <label className="field f-3 readonly">
          <div className="label-row">
            <span className="label">Total Taxes Paid</span>
          </div>
          <input
            type="text"
            className="num"
            value={formatDollars(getTotalTaxesPaid(data))}
            readOnly
          />
          <div className="field-help">
            {(() => {
              const rate = getEffectiveTaxRate(data);
              return rate != null
                ? `${rate.toFixed(1)}% effective rate on gross income`
                : 'Federal + State + FICA';
            })()}
          </div>
        </label>
      </div>

      {/* Living Expenses now comes FIRST (Jake, 2026-07-23): advisors ask for
          it first, and having it below the table meant he entered it once at
          the top, then found the real field lower down and entered it twice.
          The table that follows is therefore "Other Expenses". */}
      <div className="subhead">Annual Expenses</div>
      <div className="grid">
        <Field
          field={{ key: 'livingExpenses', label: 'Living Expenses', type: 'currency', width: 'medium' }}
          value={sectionData.livingExpenses}
          onChange={val => update('taxesExpenses.livingExpenses', val)}
        />
        {/* Living Expenses is a scalar field, not a table row, so it was the one
            expense line with nowhere to hang a note — advisors write "Breakeven"
            and the like against it on the old PQ (Alex Valles 2026-07-29, Jake
            before him). It fills the Notes column on the Classic sheet, which
            renders that cell blank for this row today. */}
        <Field
          field={{ key: 'livingExpensesNotes', label: 'Notes', type: 'text', width: 'wide' }}
          value={sectionData.livingExpensesNotes}
          onChange={val => update('taxesExpenses.livingExpensesNotes', val)}
        />
      </div>

      <div className="subhead">Other Expenses</div>
      <DataTable sectionId="taxesExpenses" tableDef={EXPENSES_TABLE} />

      <div className="grid" style={{ marginTop: 14 }}>
        <label className="field f-3 readonly">
          <div className="label-row">
            <span className="label">Total Expenses</span>
          </div>
          <input
            type="text"
            className="num"
            value={formatDollars(getTotalExpenses(data))}
            readOnly
          />
        </label>
      </div>
    </SectionShell>
  );
}
